import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BulkSelectEmployeeDialogComponent } from '@app/components';
import { Employee } from '@app/models/employee/employee.model';
import { Project } from '@app/models/time-tracking/project.model';
import { AuthService, NotifyService } from '@app/services';
import { ProjectEmployeeAssignmentService } from '@time-tracking/services/project-employee-assignment.service';
import { Translatable } from '@app/types/translatable.type';
import { Platform } from '@app/classes';
import { ProjectOwnershipService } from '@time-tracking/services/project-ownership.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ProjectOwnership } from '@models/time-tracking/project-ownership.model';

@Component({
    templateUrl: './time-tracking-store.template.html',
})
export class TimeTrackingStoreView implements OnInit, OnDestroy {
    @ViewChild('selectEmployees', { static: true }) selectEmployees: BulkSelectEmployeeDialogComponent;

    timeTrackingProject = new Project();
    employees: Employee[] = [];
    employeesOriginalAssignments: Employee[] = [];

    approvalFlows: { [key: string]: Translatable } = {
        automatic: 'time-tracking.approvalFlows.automatic',
        manager: 'time-tracking.approvalFlows.manager',
        owner: 'time-tracking.approvalFlows.owner',
    };

    potentialOwners: Employee[] = [];
    owners: Employee[] = [];
    ownersFC: FormControl<number[]> = new FormControl();

    isLoading = false;
    isEditPage = Boolean(this.route.snapshot.data.models);
    isAdmin = false;

    private _onDestroy = new Subject<void>();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private notifyService: NotifyService,
        private projectEmployeeAssignmentService: ProjectEmployeeAssignmentService,
        private location: Location,
        private authService: AuthService,
        private projectOwnershipService: ProjectOwnershipService
    ) {
        this.isAdmin = this.authService.can(Platform.timeTracking.permission.view);

        if (this.isEditPage) {
            this.timeTrackingProject = this.route.snapshot.data.models.get('project');

            this.employees = this.timeTrackingProject.projectAssignments.map(
                (projectAssignment) => projectAssignment.employee
            );
            this.createEmployeesOriginalAssignments();
        }
    }

    async ngOnInit(): Promise<void> {
        // stop sneaky users from accessing this page
        // when the project is archived
        if (this.timeTrackingProject.isArchived) {
            this.router.navigate(['..'], { relativeTo: this.route });
            return;
        }
        await this.initProjectOwners();
    }
    ngOnDestroy(): void {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    async onSave(form: NgForm): Promise<void> {
        if (!this.submitAndValidate(form) || this.isLoading) {
            return;
        }

        this.isLoading = true;
        try {
            this.timeTrackingProject = await this.timeTrackingProject.save();

            const attachEmployees = this.employees.filter((employee) => {
                return !this.employeesOriginalAssignments.some((originalEmployee) => {
                    return employee.id === originalEmployee.id;
                });
            });

            const detachEmployees = this.employeesOriginalAssignments.filter((originalEmployee) => {
                return !this.employees.some((employee) => {
                    return employee.id === originalEmployee.id;
                });
            });

            await this.projectEmployeeAssignmentService.assign(
                this.projectEmployeeAssignmentService.createForProject(this.timeTrackingProject, attachEmployees),
                this.projectEmployeeAssignmentService.createForProject(this.timeTrackingProject, detachEmployees)
            );

            this.createEmployeesOriginalAssignments();

            // Update the owners of the project
            // Only attempt if we are the admin
            if (this.isAdmin) {
                await this.projectOwnershipService.batch(
                    this.timeTrackingProject.id,
                    this.isOwnerApprovalFlow() ? this.owners : []
                );
            }

            this.notifyService.success('time-tracking.createProjectPage.saveSuccess');

            if (this.isEditPage) {
                // if current route is projects/:project/edit
                this.router.navigate(['..'], { relativeTo: this.route });
                return;
            }

            // if current route is projects/create
            this.router.navigate(['..', this.timeTrackingProject.id], { relativeTo: this.route });
        } catch (err) {
            this.notifyService.error('time-tracking.createProjectPage.saveFailed');
            this.isLoading = false;
        }
    }

    onCancel(): void {
        this.location.back();
    }

    isOwnerApprovalFlow(): boolean {
        return this.timeTrackingProject.approvalFlow === 'owner';
    }

    async setupProjectEmployeeAssignments(employees: Employee[]): Promise<void> {
        this.employees = employees;
    }

    async onDeleteAssignment(employee: Employee): Promise<void> {
        this.employees = this.employees.filter((origEmployee) => employee.id !== origEmployee.id);
    }

    private submitAndValidate(form: NgForm): boolean | null {
        if (form) {
            form.onSubmit(null);
            return form.valid;
        }

        return true;
    }

    private createEmployeesOriginalAssignments(): void {
        // This will be used to tell the diff for detaching and attaching employees
        this.employeesOriginalAssignments = [
            ...this.timeTrackingProject.projectAssignments.map((projectAssignment) => projectAssignment.employee),
        ];
    }

    private async initProjectOwners(): Promise<void> {
        this.owners = this.timeTrackingProject.projectOwnerships.map(
            (projectOwnership: ProjectOwnership) => projectOwnership.owner
        );
        this.ownersFC.setValue(this.owners.map((owner: Employee) => owner.id));

        // Get a list of Employees who could potentially be Project Owners
        const [potentialOwners] = await Employee.param('company', this.authService.company.id)
            .where('isAssignedModule', Platform.modules.timeTracking)
            .orderBy('first_name', 'ASC')
            .all();
        this.potentialOwners = potentialOwners;

        this.ownersFC.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe((ownerIds: number[]) => {
            // Return only the employees whose id matches one of the numbers in ownerIds
            this.owners = this.potentialOwners.filter((obj: Employee) => ownerIds.some((num) => num === obj.id));
        });
    }
}
